import { Component, ElementRef, type OnDestroy, type OnInit, inject, output, signal, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { TURNSTILE_SITE_KEY } from './turnstile-widget.tokens';

declare const turnstile: {
  render: (container: HTMLElement, options: TurnstileOptions) => string;
  reset: (widgetId: string) => void;
  remove: (widgetId: string) => void;
} | undefined;

interface TurnstileOptions {
  sitekey: string;
  callback?: (token: string) => void;
  'expired-callback'?: () => void;
  'error-callback'?: () => void;
  theme?: 'light' | 'dark' | 'auto';
  size?: 'normal' | 'compact' | 'flexible';
  language?: string;
  appearance?: 'always' | 'execute' | 'interaction-only';
}

type WidgetState = 'loading' | 'ready' | 'solved' | 'expired' | 'error';

@Component({
  selector: 'app-turnstile-widget',
  imports: [],
  template: `
    <div class="turnstile-container min-h-[65px] flex items-center justify-center">
      <!-- Loading state while script loads -->
      @if (widgetState() === 'loading') {
      }

      <!-- Ready / idle — widget is rendered and waiting for interaction -->
      @if (widgetState() === 'ready') {
      }

      <!-- Solved — challenge completed successfully -->
      @if (widgetState() === 'solved') {
      }

      <!-- Error state -->
      @if (widgetState() === 'error') {
      }

      <!-- The Turnstile widget renders here (invisible in interaction-only mode) -->
      <div #widgetContainer class="turnstile-render-target"></div>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TurnstileWidgetComponent implements OnInit, OnDestroy {
  private readonly siteKey = inject(TURNSTILE_SITE_KEY);

  readonly widgetContainer = viewChild.required<ElementRef<HTMLDivElement>>('widgetContainer');
  readonly widgetState = signal<WidgetState>('loading');

  /** Emits the Turnstile token when the user successfully completes the challenge. */
  readonly resolved = output<string>();

  /** Emits when the Turnstile token expires, requiring a new challenge. */
  readonly expired = output<void>();

  /** Emits when the widget encounters an error. */
  readonly errored = output<string>();

  /** Widget ID returned by turnstile.render() — needed for reset/remove. */
  private widgetId: string | null = null;

  /** Tracks whether the component has been destroyed. */
  private destroyed = false;

  ngOnInit(): void {
    this.loadAndRender();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.cleanupWidget();
  }

  /** Reset the Turnstile widget (call after a form submission error). */
  reset(): void {
    if (this.widgetId && typeof turnstile !== 'undefined') {
      turnstile.reset(this.widgetId);
    }
    this.widgetState.set('ready');
  }

  private loadAndRender(): void {
    if (typeof turnstile !== 'undefined') {
      // Already loaded
      this.renderWidget();
      return;
    }

    const scriptId = 'cf-turnstile-script';
    const existing = document.getElementById(scriptId) as HTMLScriptElement | null;

    if (existing) {
      // Script element exists but hasn't loaded yet
      existing.addEventListener('load', () => this.renderWidget(), { once: true });
      existing.addEventListener('error', () => this.onScriptError(), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.id = scriptId;
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (!this.destroyed) {
        this.renderWidget();
      }
    };
    script.onerror = () => {
      if (!this.destroyed) {
        this.onScriptError();
      }
    };
    document.head.appendChild(script);
  }

  private renderWidget(): void {
    if (this.destroyed) return;

    const container = this.widgetContainer()?.nativeElement;
    if (!container) return;

    // Clear any previous content (in case of re-render)
    container.innerHTML = '';

    try {
      const turnstileApi = turnstile;
      if (!turnstileApi) {
        // turnstile still not available — script may have failed silently
        this.widgetState.set('error');
        this.errored.emit('Turnstile API no disponible');
        return;
      }

      this.widgetId = turnstileApi.render(container, {
        sitekey: this.siteKey,
        callback: (token: string) => {
          if (!this.destroyed) {
            this.widgetState.set('solved');
            this.resolved.emit(token);
          }
        },
        'expired-callback': () => {
          if (!this.destroyed) {
            this.widgetState.set('expired');
            this.expired.emit();
          }
        },
        'error-callback': () => {
          if (!this.destroyed) {
            this.widgetState.set('error');
            this.errored.emit('Error en el widget de verificación');
          }
        },
        theme: 'dark',
        size: 'normal',
        appearance: 'always',
      });

      // Widget rendered successfully — hide the loading state
      if (!this.destroyed) {
        this.widgetState.set('ready');
      }
    } catch {
      if (!this.destroyed) {
        this.widgetState.set('error');
        this.errored.emit('Error al renderizar el widget de verificación');
      }
    }
  }

  private onScriptError(): void {
    if (this.destroyed) return;
    this.widgetState.set('error');
    this.errored.emit('No se pudo cargar el script de verificación');
  }

  private cleanupWidget(): void {
    if (this.widgetId && typeof turnstile !== 'undefined') {
      try {
        turnstile.remove(this.widgetId);
      } catch {
        // Silently fail — component is being destroyed anyway
      }
    }
    this.widgetId = null;
  }
}
